import React from "react";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input, InputProps } from "@/components/ui/input";
import { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";
import { cn } from "@/lib/utils";
import FormFieldTooltip from "./FormFieldTooltip";

type FormFieldInputPropsType<T extends FieldValues> = Omit<InputProps, "form"> & {
  form: UseFormReturn<T, unknown, undefined>;
  placeholder: string;
  label?: string;
  name: FieldPath<T>;
  description?: string;
  tooltip?: string;
  inputType?: React.InputHTMLAttributes<T>["type"];
  prefixElement?: React.ReactElement;
  sufixElement?: React.ReactElement;
  onChange?: React.ChangeEventHandler;
  className?: string;
};

function FormFieldInput<T extends FieldValues>({
  form,
  description,
  tooltip,
  placeholder,
  label,
  name,
  inputType,
  sufixElement,
  prefixElement,
  onChange,
  className,
  ...props
}: FormFieldInputPropsType<T>) {
  return inputType === "file" ? (
    <FormField
      control={form.control}
      name={name}
      render={({ field: { value, onChange, ...fieldProps }, fieldState }) => (
        <FormItem className={className}>
          <FormLabel>
            {label}
            {tooltip && <FormFieldTooltip content={tooltip} />}
          </FormLabel>
          <FormControl>
            <div className={cn("flex items-center")}>
              <Input
                {...fieldProps}
                {...props}
                placeholder={placeholder}
                type={inputType}
                multiple={props.multiple}
                aria-invalid={fieldState.invalid}
                className={cn(fieldState.invalid && "border-destructive focus-visible:ring-destructive")}
                onChange={(event) => {
                  onChange(event.target.files && (props.multiple ? event.target.files : event.target.files[0]));
                }}
              />
              <div className="absolute top-0 right-0">{sufixElement}</div>
            </div>
          </FormControl>
          <FormDescription>{description}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  ) : (
    <FormField
      control={form.control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={className}>
          <FormLabel>
            {label}
            {tooltip && <FormFieldTooltip content={tooltip} />}
          </FormLabel>
          <FormControl>
            <div className={cn("flex items-center relative")}>
              <div className="absolute top-0 left-0">{prefixElement}</div>
              <Input
                className={cn(
                  sufixElement && "pr-14",
                  prefixElement && "pl-14",
                  fieldState.invalid && "border-destructive focus-visible:ring-destructive",
                )}
                placeholder={placeholder}
                type={inputType}
                {...field}
                aria-invalid={fieldState.invalid}
                onChange={(e) => {
                  onChange ? onChange(e) : field.onChange(e);
                }}
                {...props}
              />
              <div className="absolute top-0 right-0">{sufixElement}</div>
            </div>
          </FormControl>
          <FormDescription>{description}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export default FormFieldInput;
